import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconBell, IconChecks } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client';
import { useSession } from '../app/session';
import { formatRelative } from '../lib/format';
import { SEVERITY_COLORS } from '../theme';

export function NotificationBell() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { organisation, can } = useSession();

  const { data } = useQuery({
    queryKey: ['notifications', 'unread', organisation?.slug],
    queryFn: async () => unwrap(await api.GET('/api/v1/alerts/notifications/unread/')),
    enabled: Boolean(organisation) && can('alerts.view'),
    refetchInterval: 2 * 60 * 1000,
  });

  const markAllRead = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/v1/alerts/notifications/mark-all-read/')),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  if (!organisation || !can('alerts.view')) return null;

  const count = data?.count ?? 0;
  const notifications = data?.results ?? [];

  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={340}>
      <Menu.Target>
        <Tooltip label={t('alerts.notifications')}>
          <Indicator disabled={count === 0} color="red" size={16} label={count > 9 ? '9+' : count}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label={t('alerts.notifications')}
            >
              <IconBell size={18} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Group justify="space-between" px="xs" py={4}>
          <Text fz="sm" fw={600}>
            {t('alerts.notifications')}
          </Text>
          {count > 0 ? (
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconChecks size={13} />}
              loading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              {t('action.acknowledgeAll')}
            </Button>
          ) : null}
        </Group>
        <Menu.Divider />
        {notifications.length === 0 ? (
          <Text fz="sm" c="dimmed" px="sm" py="md" ta="center">
            {t('alerts.noAlerts')}
          </Text>
        ) : (
          <ScrollArea.Autosize mah={360}>
            <Stack gap={0}>
              {notifications.map((notification) => (
                <Menu.Item
                  key={notification.id}
                  component={Link}
                  to={`/${organisation.slug}/alerts`}
                >
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <Badge
                      size="xs"
                      circle
                      color={
                        SEVERITY_COLORS[notification.severity as keyof typeof SEVERITY_COLORS] ??
                        'gray'
                      }
                      mt={4}
                    >
                      {' '}
                    </Badge>
                    <div style={{ minWidth: 0 }}>
                      <Text fz="sm" fw={500} lineClamp={1}>
                        {notification.title}
                      </Text>
                      <Text fz="xs" c="dimmed" lineClamp={2}>
                        {notification.message}
                      </Text>
                      <Text fz="xs" c="dimmed" mt={2}>
                        {formatRelative(notification.fired_at)}
                      </Text>
                    </div>
                  </Group>
                </Menu.Item>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
